import { useEffect } from 'react';

export const useScrollAnimation = () => {
  useEffect(() => {
    const observerOptions: IntersectionObserverInit = {
      threshold: 0.05,
      rootMargin: '50px 0px -10% 0px',
    };

    const observed = new WeakSet<Element>();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            entry.target.classList.remove('will-animate');
            entry.target.classList.add('animate-in');
          });
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const observeElements = () => {
      document.querySelectorAll<Element>('.section, .card, .animate-on-scroll').forEach((el) => {
        if (observed.has(el)) return;
        observed.add(el);

        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (isVisible) {
          el.classList.add('animate-in');
        } else {
          el.classList.add('will-animate');
          observer.observe(el);
        }
      });
    };

    // Initial pass — runs immediately so above-the-fold content appears right away
    observeElements();

    // Re-run whenever Suspense resolves and mounts new sections
    const mutationObserver = new MutationObserver(observeElements);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    // Safety net: re-check on scroll so sections loaded below fold while
    // the user was already there don't stay invisible
    const onScroll = () => {
      document.querySelectorAll<Element>('.section.will-animate').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.remove('will-animate');
          el.classList.add('animate-in');
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
};

export default useScrollAnimation;
